import { useState, useEffect } from "react";
import { Sparkles, Check, Loader2, AlertCircle, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  getAdaptationsForContent,
  generateAdaptation,
  updateAdaptation,
  NetworkAdaptation,
  NETWORK_INFO
} from "@/services/data/networkAdaptations";

import { TikTokIcon } from "@/components/ui/TikTokIcon";

interface NetworkContentEditorProps {
  readonly videoContentId: string;
  readonly originalTitle: string;
  readonly originalDescription: string;
  readonly enabledNetworks: string[];
  readonly onSave?: () => void;
}

const NETWORK_ICONS: Record<string, React.ReactNode> = {
  instagram: <Instagram className="h-4 w-4" />,
  tiktok: <TikTokIcon className="h-4 w-4" />,
  facebook: <Facebook className="h-4 w-4" />,
  linkedin: <Linkedin className="h-4 w-4" />,
  threads: <span className="text-xs font-bold">@</span>,
  youtube: <Youtube className="h-4 w-4" />
};

export default function NetworkContentEditor({
  videoContentId,
  originalTitle,
  originalDescription,
  enabledNetworks,
  onSave
}: NetworkContentEditorProps) {
  const [selectedNetwork, setSelectedNetwork] = useState<string>(enabledNetworks[0] || "instagram");
  const [adaptations, setAdaptations] = useState<Map<string, NetworkAdaptation>>(new Map());
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const networkInfo = NETWORK_INFO[selectedNetwork];

  useEffect(() => {
    loadAdaptations();
  }, [videoContentId]);

  useEffect(() => {
    // Update form when network changes
    const adaptation = adaptations.get(selectedNetwork);
    if (adaptation) {
      setEditTitle(adaptation.adapted_title || originalTitle);
      setEditDescription(adaptation.adapted_description || originalDescription);
    } else {
      setEditTitle(originalTitle);
      setEditDescription(originalDescription);
    }
    setHasChanges(false);
  }, [selectedNetwork, adaptations, originalTitle, originalDescription]);

  async function loadAdaptations() {
    try {
      const data = await getAdaptationsForContent(videoContentId);
      const map = new Map<string, NetworkAdaptation>();
      data.forEach(a => map.set(a.network, a));
      setAdaptations(map);
    } catch (error) {
      console.error("Error loading adaptations:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerate() {
    setGenerating(true);
    try {
      const result = await generateAdaptation(
        videoContentId,
        selectedNetwork,
        originalTitle,
        originalDescription
      );
      
      // Update local state
      setEditTitle(result.adapted_title || originalTitle);
      setEditDescription(result.adapted_description || originalDescription);
      
      // Reload adaptations to get the saved version
      await loadAdaptations();
      
      toast.success(`Contenu adapté pour ${NETWORK_INFO[selectedNetwork]?.label}`);
    } catch (error) {
      console.error("Error generating adaptation:", error);
      toast.error("Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateAdaptation(videoContentId, selectedNetwork, {
        adapted_title: networkInfo?.hasTitle ? editTitle : undefined,
        adapted_description: editDescription
      });
      
      // Reload to confirm
      await loadAdaptations();
      setHasChanges(false);
      
      toast.success("Modifications enregistrées");
      onSave?.();
    } catch (error) {
      console.error("Error saving adaptation:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  }

  function handleTitleChange(value: string) {
    setEditTitle(value);
    setHasChanges(true);
  }

  function handleDescriptionChange(value: string) {
    setEditDescription(value);
    setHasChanges(true);
  }

  const currentAdaptation = adaptations.get(selectedNetwork);
  const isOverLimit = editDescription.length > (networkInfo?.maxDescription || 9999);
  const isTitleOverLimit = networkInfo?.maxTitle && editTitle.length > networkInfo.maxTitle;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Network selector */}
      <div className="flex items-center gap-3">
        <Label className="text-sm text-muted-foreground shrink-0">Réseau</Label>
        <Select value={selectedNetwork} onValueChange={setSelectedNetwork}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {enabledNetworks.map(network => (
              <SelectItem key={network} value={network}>
                <div className="flex items-center gap-2">
                  {NETWORK_ICONS[network]}
                  <span>{NETWORK_INFO[network]?.label || network}</span>
                  {adaptations.has(network) && (
                    <Check className="h-3 w-3 text-green-500 ml-auto" />
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={handleGenerate}
          disabled={generating}
          className="ml-auto"
        >
          {generating ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Sparkles className="h-4 w-4 mr-1" />
          )}
          {currentAdaptation ? "Régénérer" : "Adapter avec IA"}
        </Button>
      </div>

      {/* Status indicator */}
      {currentAdaptation && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Check className="h-3 w-3 text-green-500" />
          <span>
            {currentAdaptation.is_user_modified ? "Modifié manuellement" : "Généré par IA"}
            {currentAdaptation.generated_at && (
              <> le {new Date(currentAdaptation.generated_at).toLocaleDateString("fr-FR")}</>
            )}
          </span>
        </div>
      )}

      {/* Title field (YouTube only) */}
      {networkInfo?.hasTitle && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Titre {NETWORK_INFO[selectedNetwork]?.label}</Label>
            <span className={`text-xs ${isTitleOverLimit ? "text-destructive" : "text-muted-foreground"}`}>
              {editTitle.length}/{networkInfo.maxTitle}
            </span>
          </div>
          <Input
            value={editTitle}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Titre pour cette plateforme..."
            className={isTitleOverLimit ? "border-destructive" : ""}
          />
          {isTitleOverLimit && (
            <div className="flex items-center gap-1 text-xs text-destructive">
              <AlertCircle className="h-3 w-3" />
              <span>Le titre dépasse la limite autorisée</span>
            </div>
          )}
        </div>
      )}

      {/* Description field */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Description {NETWORK_INFO[selectedNetwork]?.label}</Label>
          <span className={`text-xs ${isOverLimit ? "text-destructive" : "text-muted-foreground"}`}>
            {editDescription.length}/{networkInfo?.maxDescription || "∞"}
          </span>
        </div>
        <Textarea
          value={editDescription}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          placeholder="Description adaptée pour cette plateforme..."
          className={`min-h-[150px] resize-none ${isOverLimit ? "border-destructive" : ""}`}
        />
        {isOverLimit && (
          <div className="flex items-center gap-1 text-xs text-destructive">
            <AlertCircle className="h-3 w-3" />
            <span>La description dépasse la limite de {networkInfo?.maxDescription} caractères</span>
          </div>
        )}
      </div>

      {/* Save button */}
      {hasChanges && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving || isOverLimit || isTitleOverLimit}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Check className="h-4 w-4 mr-1" />
            )}
            Enregistrer
          </Button>
        </div>
      )}
    </div>
  );
}
